// client/map/updaters/SilosUpdater.ts
import type { Map as MapType } from "maplibre-gl";
import type { MapUpdater, UpdaterContext } from "./types";
import type { SiloPlatform } from "../types";
import type { SilosManager } from "../managers/silos/SilosManager";

export class SilosUpdater implements MapUpdater {
  readonly name = "silos";

  // 🌟 TOPIC REALIGNMENT: Unified topic registration parameters across both properties
  readonly topics = ["skopa/silo:batch"];

  private silosManager: SilosManager | null = null;

  public getSupportedTopics(): string[] {
    return this.topics;
  }

  constructor(private ctx: UpdaterContext) {}

  async initialise(map: MapType) {
    console.log("⚙️ [SilosUpdater] Initializing rendering contexts...");
  }

  /**
   * Safe injection hook called by MapEngine once SilosManager is ready
   */
  setSilosManager(manager: SilosManager) {
    this.silosManager = manager;
    console.log(
      "🔌 [SilosUpdater] SilosManager pipeline connected successfully",
    );
  }

  /**
   * Primary entry-point handler for incoming WebSocket network packets
   */
  async handleMessage(topic: string, payload: any) {
    if (!this.silosManager) {
      console.warn(
        "⚠️ [SilosUpdater] Message dropped: SilosManager not connected",
      );
      return;
    }

    // 🌟 SAFE SAFEGUARD: Extract raw platform payload whether it's nested or a direct array
    let rawPlatforms = payload;
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      rawPlatforms =
        payload.platforms || payload.data || payload.silos || payload;
    }

    const platforms: SiloPlatform[] = Array.isArray(rawPlatforms)
      ? rawPlatforms
      : [rawPlatforms];

    switch (topic) {
      case "skopa/silo:batch":
        await this.handleBatchUpdate(platforms);
        break;

      default:
        console.debug(
          `[SilosUpdater] Unhandled topic signature fallback: ${topic}`,
        );
        // Fallback processing channel if topic arrives slightly mutated
        await this.handleBatchUpdate(platforms);
        break;
    }
  }

  /**
   * Forwards the parsed array of structured silo nodes down to the state engine
   */
  private async handleBatchUpdate(platforms: SiloPlatform[]) {
    try {
      if (!platforms || platforms.length === 0 || !platforms[0]?.position) {
        console.warn(
          "⚠️ [SilosUpdater] Dropping update: Payload structure is missing valid geometric tracking nodes.",
        );
        return;
      }
      await this.silosManager!.updateSilosBatch(platforms);
    } catch (error) {
      console.error(
        "❌ [SilosUpdater] Failed to process incoming silos batch:",
        error,
      );
    }
  }

  destroy() {
    console.log("🧹 [SilosUpdater] Disconnecting state references...");
    this.silosManager = null;
  }
}
